/* System defined core library */
import { Component, EventEmitter, Input, OnInit, Output, QueryList, ViewChildren } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

/* User defined library/components */
import { BootstrapParams } from '../../../../bootstrap-params';
import { HttpService } from '../../../core/http.service';
import { SelectedExamDetailService } from '../../../scheduling/shared/services/selected-exam-detail.service';
import { DynamicControlService } from '../../services/dynamic.control.service';
import { DynamicFormControlComponent } from '../dynamic-form-control/dynamic-form-control.component';

/* User defined models */
import { ProSchedulerDropdown } from '../../models/common/dropdown.model';
import { DynamicFormBase } from '../../models/common/dynamic-form-base.model';
import { Candidate } from '../../models/scheduling/candidate.model';
import { Country } from '../../models/scheduling/country.model';


@Component({
  selector: 'dynamic-form',
  templateUrl: './dynamic-form.component.html',
  providers: [DynamicControlService]
})
export class DynamicFormComponent implements OnInit {

  @ViewChildren('control') controls: QueryList<DynamicFormControlComponent>;

  /* User defined private/public variables  */
  public form: FormGroup;
  public selectedCandidate: Candidate = new Candidate();
  public countries: any;

  /* Input-OutPut parameters decalartions */
  @Output() isDynamicFormValid = new EventEmitter<boolean>();
  @Output() inputItemList: any[] = [];
  @Input() frmInputData: DynamicFormBase<any>[] = [];
  @Input() formStyle: string;

  /* Service subscriptions */
  candidate$: Subscription;
  countries$: Subscription;

  /* Constructor declaration with required injected services */
  constructor(
    private bootstrapParams: BootstrapParams,
    private dynamicControlService: DynamicControlService,
    private httpService: HttpService,
    private selectedExamDetailService: SelectedExamDetailService
  ) {
    this.candidate$ = selectedExamDetailService.candidate.subscribe(
      candidate => {
        this.selectedCandidate = candidate;
      });

    this.countries$ = selectedExamDetailService.countries.subscribe(
      countries => {
        this.countries = countries;
      });
  }

  /* Initialize the component & call require methods when component loaded first time. */
  ngOnInit() {
    this.form = this.dynamicControlService.toFormGroup(this.frmInputData, this.selectedCandidate);

    if (this.selectedCandidate && this.selectedCandidate.defaultDetails && 'country' in this.selectedCandidate.defaultDetails && this.selectedCandidate.defaultDetails['country']) {
      const countryList = this.frmInputData.filter(input => input.key === 'COUNTRY');
      if (countryList.length) {
        const countryDropdown = <ProSchedulerDropdown>countryList[0];

        countryDropdown.setSelectedOptionInternally();
        if (countryDropdown.valueRequiresChildren) {
          this.getRegions(<Country>countryDropdown.selectedOption);
        }
      }
    }
    this.form.valueChanges.subscribe(() => {
      this.setIsValidDynaicForm(this.getFormValidity()); // Emitted form valid state to parent
    });
  }


  /* Emit parent component whether form is valid or not */
  setIsValidDynaicForm(event: boolean) {
    this.isDynamicFormValid.emit(event);
  }

  /* Refresh state dropdown if country is change*/
  refreshDOM(event: any) {
    if (event['type'] === 'CountryChanged') {
      this.getRegions(event['value']);
    }
  }

  /* Get region list from API */
  getRegions(country: Country) {
    if (country && country['Children']) {
      this.httpService.get(country['Children'], this.bootstrapParams.Token)
        .pipe(map(this.getResultArray))
        .subscribe((provinces: any[]) => {
          this.frmInputData.forEach((input) => {
            if (input.displayFlag) {
              // Unsets county_name/state dropdown and sets required in the formgroup
              if ((input.key === 'COUNTY_NAME' || input.key === 'STATE') && input.controlType === 'dropdown') {
                input.values = provinces ? provinces['results'] : [];
                if (!this.selectedCandidate.details[input.key]) {
                  input.value = null;
                  this.form.controls[input.key].setValue('-1');
                }
                this.form.controls[input.key].setValidators([Validators.required, this.validateProvince]);
                this.form.controls[input.key].updateValueAndValidity();
                this.form.updateValueAndValidity();
              } else if (input.key === 'ZIPCODE' || input.key === 'COUNTY_NAME' || input.key === 'STATE') {
                this.form.controls[input.key].setValidators([Validators.required, Validators.pattern(input.pattern)]);
                this.form.controls[input.key].updateValueAndValidity();
                this.form.updateValueAndValidity();
              }

              // If field is already disabled, it must be validated again to reflect changes to the required state
              if (this.form.controls[input.key].disabled) {
                const controlField = this.controls.find((control) => control.ctrlInputData.key === input.key);
                controlField.validateDisabledInput();
              }
            }
          });
          // Emits form validity based on if all required demographics (including ZIPCODE/COUNTY_NAME/STATE if they exist) are correctly filled out / have a value selected
          // NOTE: dynamic-form-control does its check for validity BEFORE this this value is set
          this.isDynamicFormValid.emit(this.getFormValidity());

          this.selectedExamDetailService.setFormInputData(this.frmInputData);
        });
    } else {
      this.frmInputData.forEach((input) => {
        if (input.displayFlag) {
          if ((input.key === 'COUNTY_NAME' || input.key === 'STATE') && input.controlType === 'dropdown') {
            // Unsets county_name/state dropdown and makes it not Required if not required in the template
            input.value = null;
            input.values = [];
            this.form.controls[input.key].setValidators(input.required ? [Validators.required, this.validateProvince] : []);
            this.form.controls[input.key].setValue('-1');
            this.form.controls[input.key].updateValueAndValidity();
            this.form.updateValueAndValidity();
          } else if (input.key === 'ZIPCODE' || input.key === 'COUNTY_NAME' || input.key === 'STATE') {
            // Unsets county_name/state dropdown and makes it not Required if not required in the template
            this.form.controls[input.key].setValidators(input.required ? [Validators.required, Validators.pattern(input.pattern)] : Validators.pattern(input.pattern));
            this.form.controls[input.key].updateValueAndValidity();
            this.form.updateValueAndValidity();
          }

          // If field is already disabled, it must be validated again to reflect changes to the required state
          if (this.form.controls[input.key].disabled) {
            const controlField = this.controls.find((control) => control.ctrlInputData.key === input.key);
            controlField.validateDisabledInput();
          }
        }
      });
      // Updates form validity based on if all required demographics are correctly filled out / have a value selected
      // NOTE: dynamic-form-control does its check for validity AFTER this this value is set, overwriting it.
      this.isDynamicFormValid.emit(this.getFormValidity());

      this.selectedExamDetailService.setFormInputData(this.frmInputData);
    }
  }

  validateProvince(control: FormControl) {
    if (control && control.value === '-1') {
      return { invalidProvince: true };
    } else {
      return null;
    }
  }

  getResultArray(apiResponse: any): any[] {
    const resultArray = apiResponse;
    return resultArray || [];
  }

  getFormValidity(): boolean {
    // Since disabled controls are excluded from form validation, we must check if we have any problems with readonly fields in addition to the overall form validity.
    // If all form controls are disabled, the overall form status becomes disabled instead of valid/invalid
    return !this.getInvalidReadOnly() && (this.form.disabled ? true : this.form.valid);
  }

  getInvalidReadOnly(): boolean {
    // Check if any children controls are invalid and disabled
    return this.controls.some((control) => control.invalidReadOnly);
  }
}
